import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  BookOpen, 
  FileText, 
  ClipboardCheck,
  ClipboardList,
  Calendar,
  Download,
  Target,
  Layers,
  CheckCircle2,
  Printer,
  GraduationCap,
  Dna,
  Microscope,
  FlaskConical,
  Leaf,
  Bug,
  Heart,
  List,
  Sparkles,
  Globe,
  Brain,
  PenTool,
  Clock
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { 
  Accordion, 
  AccordionContent, 
  AccordionItem, 
  AccordionTrigger 
} from "@/components/ui/accordion";
import { toast } from 'sonner';

import { biologyCurriculum } from '@/data/biologyCurriculum';
import { biologyWeeklyLessons } from '@/data/biologyWeeklyLessons';
import { 
  year11BiologyUnitTests,
  year12BiologyUnitTests,
  year13BiologyUnitTests
} from '@/data/biologyUnitTests';
import { 
  year11BiologyStudentNotes,
  year12BiologyStudentNotes,
  year13BiologyStudentNotes
} from '@/data/biologyStudentNotes';
import AILessonPlanGenerator from '@/components/curriculum/AILessonPlanGenerator';
import TermExamGenerator from '@/components/curriculum/TermExamGenerator';
import QuickPrintAll from '@/components/curriculum/QuickPrintAll';
import { FileQuestion, Files } from 'lucide-react';
import LessonPlanGenerator from '@/components/curriculum/LessonPlanGenerator';
import SubjectSchemeOfWork from '@/components/curriculum/SubjectSchemeOfWork';
import { getCurrentUser } from '@/data/users';
import apiService from '@/services/api';

export default function BiologyCurriculumPage() {
  const [selectedYear, setSelectedYear] = useState("Year 11");
  const [activeTab, setActiveTab] = useState("curriculum");
  const [selectedTerm, setSelectedTerm] = useState("term1");
  const [teacherYears, setTeacherYears] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const navigate = useNavigate();
  const currentUser = getCurrentUser();

  useEffect(() => {
    const fetchTeacherClasses = async () => {
      try {
        const classes = await apiService.getTeacherClasses(true);
        const years = [...new Set(classes
          .filter(c => c.year >= 11 && c.year <= 13 && 
            (c.subject?.toLowerCase().includes('biology') || 
             c.subject?.toLowerCase().includes('bio')))
          .map(c => `Year ${c.year}`)
        )].sort((a, b) => {
          const yearA = parseInt(a.replace('Year ', ''));
          const yearB = parseInt(b.replace('Year ', ''));
          return yearA - yearB;
        });
        
        setTeacherYears(years.length > 0 ? years : ["Year 11", "Year 12", "Year 13"]);
        if (years.length > 0) {
          setSelectedYear(years[0]);
        }
      } catch (error) {
        console.error('Error fetching teacher classes:', error);
        setTeacherYears(["Year 11", "Year 12", "Year 13"]);
      } finally {
        setLoading(false);
      }
    };

    fetchTeacherClasses();
  }, []);

  const strandIcons = {
    0: Microscope,  // Cellular Biology
    1: Dna,         // Genetics
    2: Heart,       // Organism Level Biology
    3: Leaf,        // Environmental Biology
    4: Brain        // Variation and Evolution
  };

  const strandColors = ["blue", "purple", "red", "green", "amber"];

  // Get curriculum data for selected year
  const getCurriculumData = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    return biologyCurriculum[yearKey] || biologyCurriculum.year11;
  };

  // Get unit tests for selected year
  const getUnitTests = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const testsMap = {
      'year11': year11BiologyUnitTests,
      'year12': year12BiologyUnitTests,
      'year13': year13BiologyUnitTests
    };
    return testsMap[yearKey] || year11BiologyUnitTests;
  };

  // Get unit tests for year (formatted for TermExamGenerator)
  const getUnitTestsForYear = () => {
    const tests = getUnitTests();
    // Return all tests from all terms combined
    const allTests = [];
    ['term1', 'term2', 'term3', 'term4'].forEach(term => {
      if (tests[term]?.tests) {
        allTests.push(...tests[term].tests);
      }
    });
    return allTests;
  };

  // Get student notes for selected year
  const getStudentNotes = () => {
    const yearKey = selectedYear.toLowerCase().replace(' ', '');
    const notesMap = {
      'year11': year11BiologyStudentNotes,
      'year12': year12BiologyStudentNotes,
      'year13': year13BiologyStudentNotes
    };
    return notesMap[yearKey] || year11BiologyStudentNotes;
  };

  const curriculumData = getCurriculumData();
  const unitTests = getUnitTests();
  const studentNotes = getStudentNotes();

  const printTest = (test) => {
    const printWindow = window.open('', '_blank');
    let content = `
      <html>
      <head>
        <title>${test.title}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 30px; line-height: 1.6; }
          h1 { text-align: center; color: #2563eb; margin-bottom: 5px; }
          .subtitle { text-align: center; color: #666; margin-bottom: 20px; }
          .info-box { background: #f3f4f6; padding: 15px; border-radius: 8px; margin-bottom: 20px; }
          .info-row { display: flex; justify-content: space-between; margin-bottom: 5px; }
          .instructions { margin-bottom: 25px; padding: 10px; border-left: 3px solid #2563eb; background: #eff6ff; }
          .section { margin-bottom: 25px; page-break-inside: avoid; }
          .section-title { background: #2563eb; color: white; padding: 8px 15px; font-weight: bold; margin-bottom: 15px; }
          .sample-questions { margin-top: 10px; padding-left: 20px; }
          .sample-questions li { margin-bottom: 8px; color: #374151; }
          @media print { body { padding: 20px; } .section { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>${test.title}</h1>
        <p class="subtitle">${selectedYear} Biology</p>
        
        <div class="info-box">
          <div class="info-row"><span>Duration:</span><span>${test.duration}</span></div>
          <div class="info-row"><span>Total Marks:</span><span>${test.totalMarks}</span></div>
          <div class="info-row"><span>Name:</span><span>____________________</span></div>
          <div class="info-row"><span>Date:</span><span>____________________</span></div>
        </div>
        
        ${test.sections?.map(section => `
          <div class="section">
            <div class="section-title">${section.name} (${section.marks} marks)</div>
            <p>${section.questions} questions</p>
          </div>
        `).join('') || ''}
        
        ${test.sampleQuestions ? `
          <div class="section">
            <div class="section-title">Sample Questions</div>
            <ul class="sample-questions">
              ${test.sampleQuestions.map(q => `<li>${q}</li>`).join('')}
            </ul>
          </div>
        ` : ''}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  const printCurriculum = () => {
    const printWindow = window.open('', '_blank');
    
    let content = `
      <html>
      <head>
        <title>Biology Curriculum - ${selectedYear}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #2563eb; }
          h2 { color: #374151; border-bottom: 2px solid #2563eb; padding-bottom: 5px; margin-top: 30px; }
          h3 { color: #2563eb; }
          .strand { margin-bottom: 30px; page-break-inside: avoid; }
          .topic { margin: 10px 0; padding: 10px; background: #f8fafc; border-left: 3px solid #2563eb; }
          .outcome { margin: 5px 0 5px 20px; }
          @media print { body { padding: 0; } .strand { page-break-inside: avoid; } }
        </style>
      </head>
      <body>
        <h1>Biology Curriculum</h1>
        <h2>${curriculumData.title}</h2>
        <p>${curriculumData.description}</p>
        <p><strong>Hours per Year:</strong> ${curriculumData.hoursPerYear} | <strong>Hours per Week:</strong> ${curriculumData.hoursPerWeek}</p>
        
        ${curriculumData.strands?.map((strand, index) => `
          <div class="strand">
            <h3>Strand ${index + 1}: ${strand.name} (${strand.hours} hours - ${strand.percentage}%)</h3>
            ${strand.topics?.map(topic => `
              <div class="topic">
                <strong>${topic.id}: ${topic.name}</strong>
                <p>${topic.description}</p>
                ${topic.weeks ? `<p><em>Weeks: ${topic.weeks}</em></p>` : ''}
                ${topic.learningOutcomes ? `
                  <div><strong>Learning Outcomes:</strong></div>
                  ${topic.learningOutcomes.map(lo => `<div class="outcome">• ${lo}</div>`).join('')}
                ` : ''}
              </div>
            `).join('')}
          </div>
        `).join('')}
      </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    printWindow.onload = () => printWindow.print();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading curriculum...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="biology-curriculum-page">
      {/* Header */}
      <Card className="bg-gradient-to-r from-blue-600 to-indigo-500 text-white">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-xl">
                <Dna className="h-8 w-8" />
              </div>
              <div>
                <CardTitle className="text-2xl font-bold">Biology Curriculum</CardTitle>
                <CardDescription className="text-blue-100 mt-1">
                  Senior Secondary (Years 11-13) - Vanuatu National Syllabus
                </CardDescription>
              </div>
            </div>
            
            {/* Year Selector */}
            <div className="flex items-center gap-3">
              <span className="text-blue-100 text-sm">Year Level:</span>
              <Select value={selectedYear} onValueChange={setSelectedYear}>
                <SelectTrigger className="w-[140px] bg-white/20 border-white/30 text-white" data-testid="biology-year-selector">
                  <SelectValue placeholder="Select Year" />
                </SelectTrigger>
                <SelectContent>
                  {teacherYears.map(year => (
                    <SelectItem key={year} value={year}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
      </Card>

      {/* Course Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-blue-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Calendar className="h-5 w-5 text-blue-500" />
              <div>
                <p className="text-sm text-gray-500">Hours/Year</p>
                <p className="font-semibold">{curriculumData?.hoursPerYear || 132} hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-purple-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Layers className="h-5 w-5 text-purple-500" />
              <div>
                <p className="text-sm text-gray-500">Strands</p>
                <p className="font-semibold">{curriculumData?.strands?.length || 5} Strands</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <Target className="h-5 w-5 text-green-500" />
              <div>
                <p className="text-sm text-gray-500">Assessment</p>
                <p className="font-semibold">
                  {selectedYear === "Year 11" 
                    ? "IE 60% / CA 40%" 
                    : "EE 60% / IA 40%"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500">
          <CardContent className="pt-4">
            <div className="flex items-center gap-3">
              <GraduationCap className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-sm text-gray-500">Hours/Week</p>
                <p className="font-semibold">{curriculumData?.hoursPerWeek || 4} hours</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Main Content Tabs */}
      <Card>
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid grid-cols-6 m-4">
            <TabsTrigger value="curriculum" className="flex items-center gap-2" data-testid="curriculum-tab">
              <BookOpen className="h-4 w-4" />
              Curriculum
            </TabsTrigger>
            <TabsTrigger value="schemes" className="flex items-center gap-2" data-testid="schemes-tab">
              <Clock className="h-4 w-4" />
              Schemes
            </TabsTrigger>
            <TabsTrigger value="tests" className="flex items-center gap-2" data-testid="tests-tab">
              <ClipboardCheck className="h-4 w-4" />
              Unit Tests
            </TabsTrigger>
            <TabsTrigger value="notes" className="flex items-center gap-2" data-testid="notes-tab">
              <FileText className="h-4 w-4" />
              Study Notes
            </TabsTrigger>
            <TabsTrigger value="term-exam" className="flex items-center gap-2 bg-rose-100" data-testid="term-exam-tab">
              <FileQuestion className="h-4 w-4" />
              Term Exam
            </TabsTrigger>
            <TabsTrigger value="quick-print" className="flex items-center gap-2 bg-blue-100" data-testid="quick-print-tab">
              <Files className="h-4 w-4" />
              Quick Print
            </TabsTrigger>
            <TabsTrigger value="lesson-plans" className="flex items-center gap-2 bg-red-100" data-testid="lesson-plans-tab">
              <PenTool className="h-4 w-4" />
              Lesson Plans
            </TabsTrigger>
            <TabsTrigger value="ai-lesson" className="flex items-center gap-2 bg-amber-100" data-testid="ai-lesson-tab">
              <Sparkles className="h-4 w-4" />
              AI Generator
            </TabsTrigger>
          </TabsList>

          {/* Schemes Tab - Weekly Scheme of Work */}
          <TabsContent value="schemes" className="p-4">
            <SubjectSchemeOfWork
              subject="Biology"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={biologyWeeklyLessons}
              subjectColor="red"
            />
          </TabsContent>

          {/* Term Exam Tab */}
          <TabsContent value="term-exam" className="p-4">
            <TermExamGenerator
              subject="Biology"
              year={parseInt(selectedYear.replace('Year ', ''))}
              unitTests={getUnitTestsForYear()}
              subjectColor="red"
            />
          </TabsContent>

          {/* Quick Print Tab */}
          <TabsContent value="quick-print" className="p-4">
            <QuickPrintAll
              subject="Biology"
              year={parseInt(selectedYear.replace('Year ', ''))}
              weeklyLessons={biologyWeeklyLessons}
              subjectColor="red"
            />
          </TabsContent>

          {/* Lesson Plans Tab - Full Week Mon-Fri */}
          <TabsContent value="lesson-plans" className="p-4">
            <LessonPlanGenerator 
              defaultYear={parseInt(selectedYear.replace('Year ', ''))} 
              subject="Biology"
              subjectColor="red"
              allowedYears={['11', '12', '13']}
              weeklyLessons={biologyWeeklyLessons}
            />
          </TabsContent>

          {/* Curriculum Tab */}
          <TabsContent value="curriculum" className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">{curriculumData?.title || `${selectedYear} Biology`}</h3>
                <p className="text-gray-500">{curriculumData?.description || 'Understanding biological systems and processes'}</p>
              </div>
              <Button variant="outline" onClick={printCurriculum}>
                <Printer className="h-4 w-4 mr-2" />
                Print Curriculum
              </Button>
            </div>
            
            <Accordion type="multiple" className="space-y-3">
              {curriculumData?.strands?.map((strand, index) => {
                const StrandIcon = strandIcons[index] || Microscope;
                const colorClass = strandColors[index] || "blue";
                return (
                  <AccordionItem key={strand.id} value={strand.id} className="border rounded-lg overflow-hidden">
                    <AccordionTrigger className={`px-4 py-3 bg-${colorClass}-50 hover:bg-${colorClass}-100`}>
                      <div className="flex items-center gap-3">
                        <StrandIcon className={`h-5 w-5 text-${colorClass}-600`} />
                        <Badge className={`bg-${colorClass}-500 text-white`}>
                          Strand {index + 1}
                        </Badge>
                        <span className="font-semibold">{strand.name}</span>
                        <span className="text-gray-500 text-sm ml-2">({strand.hours} hours - {strand.percentage}%)</span>
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="p-4 bg-white">
                      <div className="space-y-3">
                        {strand.topics?.map((topic, tIdx) => (
                          <div key={tIdx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-l-blue-400">
                            <div className="flex items-start gap-3">
                              <CheckCircle2 className="h-5 w-5 text-blue-500 mt-0.5" />
                              <div className="flex-1">
                                <div className="flex items-center gap-2 mb-1">
                                  <Badge variant="outline" className="text-xs">{topic.id}</Badge>
                                  <p className="font-medium">{topic.name}</p>
                                </div>
                                <p className="text-sm text-gray-500 mb-2">{topic.description}</p>
                                {topic.weeks && <Badge variant="outline" className="mr-2">Weeks {topic.weeks}</Badge>}
                                
                                {topic.learningOutcomes && topic.learningOutcomes.length > 0 && (
                                  <div className="mt-3 p-3 bg-white rounded border">
                                    <p className="text-sm font-medium text-gray-700 mb-2">Learning Outcomes:</p>
                                    <ul className="text-sm text-gray-600 space-y-1">
                                      {topic.learningOutcomes.slice(0, 5).map((outcome, oIdx) => (
                                        <li key={oIdx} className="flex items-start gap-2">
                                          <span className="text-blue-500">•</span>
                                          {outcome}
                                        </li>
                                      ))}
                                      {topic.learningOutcomes.length > 5 && (
                                        <li className="text-blue-600 font-medium">
                                          ... and {topic.learningOutcomes.length - 5} more outcomes
                                        </li>
                                      )}
                                    </ul>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                );
              })}
            </Accordion>
          </TabsContent>

          {/* Unit Tests Tab */}
          <TabsContent value="tests" className="p-4">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Unit Tests - {selectedYear}</h3>
                <p className="text-gray-500">Assessment materials and practical evaluations</p>
              </div>
              <Select value={selectedTerm} onValueChange={setSelectedTerm}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue placeholder="Select Term" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="term1">Term 1</SelectItem>
                  <SelectItem value="term2">Term 2</SelectItem>
                  <SelectItem value="term3">Term 3</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {unitTests[selectedTerm] && (
              <div className="space-y-4">
                <h4 className="font-medium text-gray-700">{unitTests[selectedTerm].title}</h4>
                <div className="grid gap-4">
                  {unitTests[selectedTerm].tests?.map((test, idx) => (
                    <Card key={idx} className="hover:shadow-md transition-shadow">
                      <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <ClipboardCheck className="h-5 w-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium">{test.title}</p>
                              <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                                <span>Duration: {test.duration}</span>
                                <span>•</span>
                                <span>Total: {test.totalMarks} marks</span>
                              </div>
                            </div>
                          </div>
                          <Button variant="outline" size="sm" onClick={() => printTest(test)}>
                            <Printer className="h-4 w-4 mr-2" />
                            Print
                          </Button>
                        </div>
                        
                        {test.sections && (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {test.sections.map((section, sIdx) => (
                              <Badge key={sIdx} variant="secondary" className="text-xs">
                                {section.name}: {section.marks} marks
                              </Badge>
                            ))}
                          </div>
                        )}
                        
                        {test.sampleQuestions && (
                          <div className="mt-3 p-3 bg-gray-50 rounded">
                            <p className="text-sm font-medium text-gray-700 mb-2">Sample Questions:</p>
                            <ul className="text-sm text-gray-600 space-y-1">
                              {test.sampleQuestions.slice(0, 3).map((q, qIdx) => (
                                <li key={qIdx}>• {q}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Practical Assessments */}
            {unitTests.practicalAssessments && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-4">Practical Assessments</h4>
                <div className="grid gap-4">
                  {unitTests.practicalAssessments.map((practical, idx) => (
                    <Card key={idx} className="border-l-4 border-l-purple-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-purple-100 rounded-lg">
                            <FlaskConical className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-medium">{practical.title}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-500 mt-1">
                              <span>Duration: {practical.duration}</span>
                              <span>•</span>
                              <span>{practical.marks} marks</span>
                            </div>
                            {practical.skills && (
                              <div className="flex flex-wrap gap-1 mt-2">
                                {practical.skills.map((skill, sIdx) => (
                                  <Badge key={sIdx} variant="outline" className="text-xs">{skill}</Badge>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Internal Assessments for Year 13 */}
            {unitTests.internalAssessments && (
              <div className="mt-6">
                <h4 className="font-medium text-gray-700 mb-4">Internal Assessment Tasks (IA 40%)</h4>
                <div className="grid gap-4">
                  {unitTests.internalAssessments.map((ia, idx) => (
                    <Card key={idx} className="border-l-4 border-l-amber-500">
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-amber-100 rounded-lg">
                            <Target className="h-5 w-5 text-amber-600" />
                          </div>
                          <div className="flex-1">
                            <p className="font-medium">{ia.title}</p>
                            <p className="text-sm text-gray-500 mt-1">{ia.description}</p>
                            <div className="flex items-center gap-3 text-sm text-gray-500 mt-2">
                              <Badge variant="outline">Strand {ia.strand}</Badge>
                              <span>{ia.marks}% of total</span>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </TabsContent>

          {/* Study Notes Tab */}
          <TabsContent value="notes" className="p-4">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-700">Study Notes - {selectedYear}</h3>
              <p className="text-gray-500">Comprehensive study materials organized by strand</p>
            </div>
            
            <div className="grid gap-4">
              {Object.entries(studentNotes).map(([strandKey, strand]) => (
                <Card key={strandKey} className="overflow-hidden">
                  <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 pb-2">
                    <div className="flex items-center gap-3">
                      {strand.image && (
                        <img 
                          src={strand.image} 
                          alt={strand.title}
                          className="w-16 h-16 object-cover rounded-lg"
                        />
                      )}
                      <div>
                        <CardTitle className="text-lg text-blue-800">{strand.title}</CardTitle>
                        <CardDescription>{strand.overview}</CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <Accordion type="single" collapsible>
                      {strand.units?.map((unit, uIdx) => (
                        <AccordionItem key={uIdx} value={unit.id}>
                          <AccordionTrigger className="text-left">
                            <div className="flex items-center gap-2">
                              <FileText className="h-4 w-4 text-blue-600" />
                              <span>{unit.title}</span>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent>
                            <div className="prose prose-sm max-w-none p-3 bg-gray-50 rounded-lg">
                              <div 
                                className="whitespace-pre-wrap text-sm"
                                dangerouslySetInnerHTML={{ 
                                  __html: unit.content
                                    .replace(/## /g, '<h3 class="text-blue-700 font-bold mt-4 mb-2">')
                                    .replace(/### /g, '<h4 class="text-blue-600 font-semibold mt-3 mb-1">')
                                    .replace(/#### /g, '<h5 class="font-medium mt-2">')
                                    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                                    .replace(/\n/g, '<br/>')
                                }}
                              />
                            </div>
                            
                            {unit.keyTerms && unit.keyTerms.length > 0 && (
                              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                                <h5 className="font-medium text-blue-800 mb-2">Key Terms</h5>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  {unit.keyTerms.map((term, tIdx) => (
                                    <div key={tIdx} className="text-sm">
                                      <span className="font-medium text-blue-700">{term.term}:</span>{' '}
                                      <span className="text-gray-600">{term.definition}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            
                            {unit.practiceQuestions && unit.practiceQuestions.length > 0 && (
                              <div className="mt-4 p-3 bg-purple-50 rounded-lg">
                                <h5 className="font-medium text-purple-800 mb-2">Practice Questions</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {unit.practiceQuestions.map((q, qIdx) => (
                                    <li key={qIdx}>• {q}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                            
                            {unit.examTips && unit.examTips.length > 0 && (
                              <div className="mt-4 p-3 bg-amber-50 rounded-lg">
                                <h5 className="font-medium text-amber-800 mb-2">Exam Tips</h5>
                                <ul className="text-sm text-gray-700 space-y-1">
                                  {unit.examTips.map((tip, tipIdx) => (
                                    <li key={tipIdx}>💡 {tip}</li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      ))}
                    </Accordion>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* AI Lesson Plans Tab */}
          <TabsContent value="ai-lesson" className="p-4">
            <AILessonPlanGenerator 
              defaultSubject="biology"
              defaultYear={parseInt(selectedYear.replace('Year ', ''))}
              schoolType="secondary"
              allowedYears={teacherYears}
              allowedSubjects={['biology']}
            />
          </TabsContent>
        </Tabs>
      </Card>
    </div>
  );
}
